import { paperClasses } from '@mui/material';
import Alert, { alertClasses } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useEffect } from 'react';
import { useState } from 'react';

import { SiteEquipmentStats } from '~hooks/useSiteEquipmentStats/models';
import { useSiteEquipmentStats } from '~hooks/useSiteEquipmentStats/useSiteEquipmentStats';
import { SiteBasic } from '~hooks/useSites/models';
import { useStores } from '~store';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  BothSiteIcon,
  DropoffSiteIcon,
  PickupSiteIcon,
  StagingSiteIcon,
} from './SiteIcons';

// TODO: move to shared constants folder
const emDash = '-';

function KeyValuePair({
  keyName,
  value,
}: {
  keyName: string;
  value: string | null | undefined;
}) {
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {keyName}
      </Typography>
      <Typography variant="body2">{value ?? emDash}</Typography>
    </>
  );
}

function Break() {
  return (
    <Box
      sx={(theme) => ({
        gridColumn: 'span 2',
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
      })}
    />
  );
}

const getSVGIconByType = (type: WaypointType | 'both') => {
  if (type === WaypointType.PICKUP) {
    return <PickupSiteIcon />;
  } else if (type === WaypointType.DROP_OFF) {
    return <DropoffSiteIcon />;
  } else if (type === WaypointType.STAGING) {
    return <StagingSiteIcon />;
  } else return <BothSiteIcon />;
};

const getSiteType = (type: WaypointType | 'both') => {
  if (type === WaypointType.PICKUP) {
    return 'Pickup';
  } else if (type === WaypointType.DROP_OFF) {
    return 'Drop Off';
  } else if (type === WaypointType.STAGING) {
    return t('waypoint_type.staging');
  } else return 'Both';
};

export const SiteStatPopoverContent = ({
  site,
  type,
  isDefaultSite = false,
}: {
  site: SiteBasic;
  type: WaypointType | 'both';
  isDefaultSite?: boolean;
}) => {
  const { isLoading, getSiteEquipmentStats } = useSiteEquipmentStats();
  const [stats, setStats] = useState<SiteEquipmentStats | null>(null);
  const { userStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const fetchStats = async () => {
    const stats = await withSentryIgnoreRules(
      SentryIgnoreRule.IGNORE_SITE_EQUIPMENT_STATS_404,
      () => {
        return getSiteEquipmentStats({ companyId, siteId: site.id });
      },
    );

    setStats(stats ?? null);
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMins = Math.floor(minutes % 60);
    return hours > 0 ? `${hours}h ${remainingMins}m` : `${remainingMins}m`;
  };

  useEffect(() => {
    fetchStats();
  }, [companyId, site.id]);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          display: 'grid',
          maxWidth: '360px',
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '240px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1 }}>
        {getSVGIconByType(type)}

        <Box sx={{ flex: 1 }}>
          <Typography
            noWrap
            variant="h6"
            sx={{ fontSize: '14px' }}
          >{`${site.name}`}</Typography>
          <Typography sx={{ fontSize: '12px' }}>{`${site.lat}, ${site.lon}`}</Typography>
        </Box>

        {isDefaultSite ? (
          <Box
            component="span"
            sx={{
              // inline-flex so the vertical padding applies regardless of the
              // parent's display (a bare inline span ignores block padding).
              display: 'inline-flex',
              alignItems: 'center',
              // Amber matches the map badge (siteIconAtlas BADGE_COLOR).
              bgcolor: '#F59E0B',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              borderRadius: '4px',
              px: 0.75,
              py: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {t('live_map.site_stats_popover.default_site')}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          display: 'grid',
          gridTemplateColumns: '2fr 3fr',
          gap: 0.5,
        }}
      >
        <KeyValuePair
          keyName={t('live_map.site_stats_popover.type')}
          value={getSiteType(type)}
        />
        <Break />
        <KeyValuePair
          keyName={t('live_map.site_stats_popover.on_site_count')}
          value={stats?.onSiteEquipmentCount.toString()}
        />
        <KeyValuePair
          keyName={t('live_map.site_stats_popover.en_route_count')}
          value={stats?.enRouteEquipmentCount.toString()}
        />
        <KeyValuePair
          keyName={t('live_map.site_stats_popover.time_on_site')}
          value={
            isNil(stats?.avgOnsiteTimeMinutes)
              ? stats?.avgOnsiteTimeMinutes
              : formatTime(stats.avgOnsiteTimeMinutes)
          }
        />
      </Box>

      {stats ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
          <Alert
            variant="outlined"
            severity="info"
            sx={(theme) => ({
              [`&.${paperClasses.root}`]: {
                bgcolor: theme.brandV2.colors.treadGray8,
                borderColor: 'transparent',
                borderRadius: theme.brandV2.borderRadius,
                px: 1,
                py: 0,
                [`& .${alertClasses.message}`]: {
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '12px',
                  fontWeight: 500,
                },
                [`& .${alertClasses.icon}`]: {
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '18px',
                  mr: 1,
                },
              },
            })}
          >
            {t('live_map.site_stats_popover.no_site_equipment_stats')}
          </Alert>
        </Box>
      )}
    </Box>
  );
};
